import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Hash, Users, ArrowLeft, Lock, Bell, BellOff, UserPlus, Link2, Check, X, Pin, Search, Bookmark } from 'lucide-react';
import { channelsApi } from '@/api/channels';
import { messagesApi } from '@/api/messages';
import { useChatStore } from '@/store/chatStore';
import { useMobile } from '@/pages/chat/ChatPage';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import MembersPanel from './MembersPanel';
import ChannelProfileModal from './ChannelProfileModal';
import ChannelSearchModal from './ChannelSearchModal';
import BulkActionBar from './BulkActionBar';
import OutboxBanner from './OutboxBanner';
import { Message, Channel } from '@/types';
import { useChannelRole } from '@/hooks/useChannelRole';
import { useSelectionStore } from '@/store/selectionStore';
import { useAuthStore } from '@/store/authStore';
import NickName from '@/components/ui/NickName';

export default function ChannelView() {
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setShowSidebar } = useMobile();
  const [showMembers, setShowMembers] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // Phase 8.1 — индекс текущего показанного закреплённого сообщения. Клик
  // по баннеру циклически переключает закрепы (как в Telegram Desktop).
  const [pinnedIdx, setPinnedIdx] = useState(0);
  const [pinnedListOpen, setPinnedListOpen] = useState(false);
  const { setActiveChannel, setChannelMessages, channelMessages, clearUnread, typing } = useChatStore();

  useEffect(() => {
    if (channelId) {
      setActiveChannel(channelId);
      clearUnread(channelId);
    }
    // Drop any leftover selection from a previously-open view
    useSelectionStore.getState().exit();
    return () => setActiveChannel(null);
  }, [channelId, setActiveChannel, clearUnread]);

  // v5.7.13 / v5.7.16 — force-refetch первой страницы при каждом open канала.
  // v5.7.13: useEffect с refetchQueries({type:'active'}).
  // v5.7.16: bug-fix — на hard reload useEffect отрабатывал ДО того как
  // useInfiniteQuery становится active (mount race), и refetch skip'ался.
  // Также type:'active' оставлял inactive queries stale. Меняем на:
  //   1. type:'all' — обновит даже если ещё не active
  //   2. queueMicrotask — отложить до конца текущего render tick,
  //      чтобы useInfiniteQuery успел register observer.
  useEffect(() => {
    if (!channelId) return;
    queueMicrotask(() => {
      void queryClient.refetchQueries({
        queryKey: ['messages', 'channel', channelId],
        type: 'all',
      });
    });
  }, [channelId, queryClient]);

  const { data: channel } = useQuery({
    queryKey: ['channel', channelId],
    queryFn: () => channelsApi.get(channelId!),
    enabled: !!channelId,
  });

  const notifyEnabled = channel?.members?.[0]?.notifyEnabled ?? true;
  const memberRole = channel?.members?.[0]?.role;
  const isOwner = memberRole === 'OWNER';

  const toggleNotifyMut = useMutation({
    mutationFn: () => channelsApi.toggleNotifications(channelId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channel', channelId] }),
  });

  // Pinned messages
  const { data: pinned } = useQuery({
    queryKey: ['pinned', channelId],
    queryFn: () => messagesApi.listPinned(channelId!),
    enabled: !!channelId,
    staleTime: 30_000,
  });

  // Subscribe to live pin/unpin events
  useEffect(() => {
    const refresh = (e: Event) => {
      const detail = (e as CustomEvent).detail as { channelId: string };
      if (detail.channelId === channelId) {
        queryClient.invalidateQueries({ queryKey: ['pinned', channelId] });
        queryClient.invalidateQueries({ queryKey: ['messages', 'channel', channelId] });
      }
    };
    window.addEventListener('message:pinned', refresh);
    window.addEventListener('message:unpinned', refresh);
    return () => {
      window.removeEventListener('message:pinned', refresh);
      window.removeEventListener('message:unpinned', refresh);
    };
  }, [channelId, queryClient]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['messages', 'channel', channelId],
    queryFn: ({ pageParam }) =>
      messagesApi.getChannelMessages(channelId!, { cursor: pageParam as string, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !!channelId,
  });

  useEffect(() => {
    if (!data || !channelId) return;
    const allMsgs = data.pages.flatMap((p) => p.data).reverse();
    setChannelMessages(channelId, allMsgs);
  }, [data, channelId, setChannelMessages]);

  const messages = channelId ? (channelMessages[channelId] ?? []) : [];
  const typingUsers = channelId ? (typing[channelId] ?? []) : [];

  // Snapshot my lastReadAt once per channel open. Backend bumps it when I
  // fetch messages, so we must capture the value BEFORE that happens;
  // otherwise everything looks "read" and there's no unread separator.
  // We keep this in state so the memo below reacts when `channel` arrives
  // from the API after the first render.
  const [lastReadSnap, setLastReadSnap] = useState<{ channelId: string; lastReadAt: string | null }>({ channelId: '', lastReadAt: null });
  useEffect(() => {
    if (!channelId) return;
    const incoming = channel?.members?.[0]?.lastReadAt ?? null;
    setLastReadSnap((prev) => {
      // New channel → always reset the snapshot
      if (prev.channelId !== channelId) return { channelId, lastReadAt: incoming };
      // Same channel: take the first non-null value we see, then lock it in
      if (prev.lastReadAt === null && incoming) return { channelId, lastReadAt: incoming };
      return prev;
    });
  }, [channelId, channel]);

  const myId = useAuthStore((s) => s.user?.id);

  // v5.7.9 — Snapshot момента open'а канала. Используется для threshold
  // проверки stale-unread. `lastReadSnap.lastReadAt` сравниваем с этим
  // моментом, а не `Date.now()` который "плавает" между ререндерами и
  // может ровно на границе дёрнуть/не дёрнуть pin'инг по-разному.
  const openedAtRef = useRef<{ channelId: string; ts: number } | null>(null);
  if (
    !openedAtRef.current ||
    openedAtRef.current.channelId !== lastReadSnap.channelId
  ) {
    openedAtRef.current = { channelId: lastReadSnap.channelId, ts: Date.now() };
  }

  // v5.7.9 — если последнее прочтение слишком старое (>24ч), unread-anchor
  // указывает на ancient историю и пользователь видит "вчерашние" сообщения
  // вместо свежих. В таком случае возвращаем `null` → MessageList pin'ит в
  // bottom (последнее). Юзер сразу видит актуальное, а скроллить вверх для
  // back-history может сам.
  // Active каналы (юзер читал в течение суток) сохраняют старое поведение:
  // pin на первое непрочитанное.
  const UNREAD_STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
  const firstUnreadId = useMemo(() => {
    if (!channelId || lastReadSnap.channelId !== channelId || !lastReadSnap.lastReadAt) return null;
    if (messages.length === 0) return null;
    const lastReadMs = new Date(lastReadSnap.lastReadAt).getTime();
    const openedAtMs = openedAtRef.current?.ts ?? Date.now();
    // Stale unread → не pin'имся на старое; bottom.
    if (openedAtMs - lastReadMs > UNREAD_STALE_THRESHOLD_MS) return null;
    // messages ascending by createdAt; find first strictly newer than lastReadAt
    // AND authored by someone else — my own messages can't be "unread to me".
    for (const m of messages) {
      if (new Date(m.createdAt).getTime() <= lastReadMs) continue;
      if (myId && m.authorId === myId) continue;
      return m.id;
    }
    return null;
  }, [channelId, messages, lastReadSnap, myId]);

  // Auto-load older pages if unread messages are further back than the first
  // loaded page. Cap at 2 extra fetches to prevent runaway loops.
  const autoLoadRef = useRef<{ channelId: string; count: number }>({ channelId: '', count: 0 });
  useEffect(() => {
    if (!channelId || lastReadSnap.channelId !== channelId || !lastReadSnap.lastReadAt) return;
    if (firstUnreadId) return; // already have an unread anchor
    if (messages.length === 0) return;
    const oldestMs = new Date(messages[0].createdAt).getTime();
    const lastReadMs = new Date(lastReadSnap.lastReadAt).getTime();
    if (oldestMs <= lastReadMs) return; // all unread already in memory, just none exist
    if (autoLoadRef.current.channelId !== channelId) {
      autoLoadRef.current = { channelId, count: 0 };
    }
    if (autoLoadRef.current.count >= 2) return;
    if (hasNextPage && !isFetchingNextPage) {
      autoLoadRef.current.count += 1;
      fetchNextPage();
    }
  }, [channelId, messages, firstUnreadId, hasNextPage, isFetchingNextPage, fetchNextPage, lastReadSnap]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleBack = () => {
    setShowSidebar(true);
    navigate('/');
  };

  if (!channelId) return null;

  const isPrivate = channel?.type === 'PRIVATE';
  const memberCount = channel?._count?.members;

  return (
    <div className="flex flex-1 min-w-0">
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header — Telegram style */}
        <div className="h-14 border-b border-surface-800 px-3 flex items-center gap-3 flex-shrink-0 bg-surface-900">
          <button
            onClick={handleBack}
            className="md:hidden btn-ghost p-1.5 text-surface-400 -ml-1"
            title="Назад"
          >
            <ArrowLeft size={20} />
          </button>

          {/* Channel icon + info (clickable for profile) */}
          <button onClick={() => setShowProfile(true)} className="w-9 h-9 rounded-full bg-brand-700 flex items-center justify-center flex-shrink-0 overflow-hidden hover:ring-2 hover:ring-brand-400 transition-all">
            {channel?.avatarUrl ? (
              <img src={`${import.meta.env.VITE_API_URL ?? (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '')}${channel.avatarUrl}`} alt="" className="w-full h-full object-cover" />
            ) : channel?.isPersonal ? <Bookmark size={16} className="text-white" />
              : isPrivate ? <Lock size={16} className="text-white" />
              : <Hash size={17} className="text-white" />}
          </button>

          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setShowProfile(true)}>
            <p className="font-semibold text-surface-100 text-sm leading-tight truncate hover:text-brand-400 transition-colors">
              {channel?.isPersonal ? 'Сохранённые' : (channel?.displayName || channel?.name || channelId)}
            </p>
            <p className="text-surface-500 text-xs leading-tight">
              {typingUsers.length > 0 ? (
                <span className="text-brand-400">
                  {typingUsers.length === 1
                    ? `${typingUsers[0]} печатает...`
                    : `${typingUsers.join(', ')} печатают...`}
                </span>
              ) : memberCount !== undefined ? (
                `${memberCount} участников`
              ) : channel?.topic ? (
                <span className="truncate">{channel.topic}</span>
              ) : null}
            </p>
          </div>

          {/* Search */}
          <button
            onClick={() => setSearchOpen(true)}
            className="btn-ghost p-2 rounded-full transition-colors text-surface-500 hover:text-brand-400"
            title="Поиск в канале"
          >
            <Search size={18} />
          </button>

          {/* Share button (owner only, not for personal) */}
          {isOwner && !channel?.isPersonal && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="btn-ghost p-2 rounded-full transition-colors text-surface-500 hover:text-brand-400"
              title="Пригласить людей"
            >
              <UserPlus size={18} />
            </button>
          )}

          {/* Notification toggle */}
          <button
            onClick={() => toggleNotifyMut.mutate()}
            className={`btn-ghost p-2 rounded-full transition-colors ${notifyEnabled ? 'text-green-400' : 'text-surface-600'}`}
            title={notifyEnabled ? 'Уведомления включены' : 'Уведомления отключены'}
          >
            {notifyEnabled ? <Bell size={18} /> : <BellOff size={18} />}
          </button>

          {/* Members — not for personal */}
          {!channel?.isPersonal && (
            <button
              onClick={() => setShowMembers((v) => !v)}
              className={`btn-ghost p-2 rounded-full transition-colors ${showMembers ? 'text-brand-400 bg-brand-900/30' : 'text-surface-500'}`}
              title="Участники"
            >
              <Users size={18} />
            </button>
          )}
        </div>

        {/* Pinned banner — клик циклически переключает закрепы; клик по
            счётчику открывает модалку «все закреплённые» */}
        {pinned && pinned.length > 0 && (() => {
          const idx = pinnedIdx % pinned.length;
          const cur = pinned[idx];
          return (
            <div className="border-b border-surface-800 bg-surface-900/60 px-3 py-1.5 flex items-center gap-2 text-xs">
              <Pin size={12} className="text-brand-400 flex-shrink-0" />
              <button
                type="button"
                onClick={() => setPinnedIdx((i) => (i + 1) % pinned.length)}
                className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                title="Следующее закреплённое"
              >
                <span className="text-brand-400 font-semibold mr-1"><NickName user={cur.author} />:</span>
                <span className="text-surface-300 truncate">{cur.content}</span>
              </button>
              <button
                type="button"
                onClick={() => setPinnedListOpen(true)}
                className="text-surface-500 hover:text-brand-400 text-[10px] flex-shrink-0"
                title="Все закреплённые"
              >
                {idx + 1}/{pinned.length}
              </button>
            </div>
          );
        })()}

        {/* Outbox banner (visible only when there are queued offline messages) */}
        <OutboxBanner />

        {/* Bulk action bar (visible only in multi-select mode) */}
        <BulkActionBar
          canDelete={memberRole === 'OWNER' || memberRole === 'ADMIN' || memberRole === 'MODERATOR'}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['messages', 'channel', channelId] })}
        />

        {/* Messages */}
        <MessageList
          key={channelId}
          messages={messages}
          onLoadMore={handleLoadMore}
          hasMore={hasNextPage}
          loading={isFetchingNextPage}
          onReply={setReplyTo}
          firstUnreadId={firstUnreadId}
          selectionScope={channelId ? { kind: 'channel', channelId } : undefined}
          chatStyle={channel?.chatStyle}
        />

        <ChannelInputArea
          channel={channel}
          channelId={channelId}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
        />
      </div>

      {showMembers && <MembersPanel channelId={channelId} />}

      {/* Channel profile modal */}
      {showProfile && channelId && (
        <ChannelProfileModal channelId={channelId} onClose={() => setShowProfile(false)} />
      )}

      {/* Invite modal for channel owner */}
      {showInviteModal && channelId && (
        <InviteModal channelId={channelId} onClose={() => { setShowInviteModal(false); setCopiedCode(null); }} copiedCode={copiedCode} setCopiedCode={setCopiedCode} />
      )}

      {searchOpen && channelId && (
        <ChannelSearchModal channelId={channelId} onClose={() => setSearchOpen(false)} />
      )}

      {pinnedListOpen && pinned && pinned.length > 0 && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setPinnedListOpen(false)}>
          <div className="bg-surface-900 border border-surface-800 rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Pin size={14} className="text-brand-400" />
              <h3 className="text-surface-100 font-semibold">Закреплённые сообщения</h3>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {pinned.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { setPinnedIdx(i); setPinnedListOpen(false); }}
                  className="w-full text-left bg-surface-800 hover:bg-surface-700 rounded-lg px-3 py-2"
                >
                  <p className="text-brand-400 text-xs font-semibold"><NickName user={m.author} /></p>
                  <p className="text-surface-300 text-sm whitespace-pre-wrap break-words line-clamp-3">{m.content}</p>
                </button>
              ))}
            </div>
            <button onClick={() => setPinnedListOpen(false)} className="btn-ghost w-full mt-4">Закрыть</button>
          </div>
        </div>
      )}
    </div>
  );
}

function InviteModal({ channelId, onClose, copiedCode, setCopiedCode }: {
  channelId: string;
  onClose: () => void;
  copiedCode: string | null;
  setCopiedCode: (v: string | null) => void;
}) {
  const queryClient = useQueryClient();

  const { data: invites, isLoading } = useQuery({
    queryKey: ['invites', channelId],
    queryFn: () => channelsApi.listInvites(channelId),
  });

  const createMutation = useMutation({
    mutationFn: () => channelsApi.createInvite(channelId, { maxUses: 0 }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invites', channelId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => channelsApi.deleteInvite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invites', channelId] }),
  });

  const copyLink = (code: string) => {
    const url = `${window.location.origin}/invite/${code}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-surface-900 border border-surface-800 rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-surface-100 font-semibold mb-4">Пригласить в канал</h3>

        {createMutation.error && (
          <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2 mb-3">
            {(createMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Недостаточно прав для создания приглашений'}
          </div>
        )}

        <button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}
          className="btn-primary w-full mb-4">
          {createMutation.isPending ? 'Создание...' : 'Создать ссылку-приглашение'}
        </button>

        {isLoading && <p className="text-surface-500 text-sm">Загрузка...</p>}

        {invites && invites.length === 0 && (
          <p className="text-surface-500 text-sm text-center">Нет активных приглашений</p>
        )}

        <div className="space-y-2 max-h-60 overflow-y-auto">
          {invites?.map((inv) => (
            <div key={inv.id} className="flex items-center gap-2 bg-surface-800 rounded-lg px-3 py-2">
              <code className="text-xs text-surface-300 truncate flex-1 font-mono">{inv.code}</code>
              <span className="text-xs text-surface-500">{inv.uses} исп.</span>
              <button onClick={() => copyLink(inv.code)} title="Копировать ссылку"
                className="w-7 h-7 rounded flex items-center justify-center text-surface-400 hover:text-brand-400 hover:bg-surface-700">
                {copiedCode === inv.code ? <Check size={14} className="text-green-400" /> : <Link2 size={14} />}
              </button>
              <button onClick={() => deleteMutation.mutate(inv.id)} title="Удалить"
                className="w-7 h-7 rounded flex items-center justify-center text-surface-400 hover:text-red-400 hover:bg-surface-700">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>

        <button onClick={onClose} className="btn-ghost w-full mt-4">Закрыть</button>
      </div>
    </div>
  );
}

// Input area with membersCanPost check and non-member join banner
function ChannelInputArea({
  channel,
  channelId,
  replyTo,
  onCancelReply,
}: {
  channel: Channel | undefined;
  channelId: string;
  replyTo: Message | null;
  onCancelReply: () => void;
}) {
  const role = useChannelRole(channelId);
  const queryClient = useQueryClient();
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  // Channel loaded but current user has no membership record → show join banner
  if (channel !== undefined && !channel.members?.[0]) {
    if (channel.type !== 'PUBLIC') {
      return (
        <div className="border-t border-surface-800 bg-surface-900 px-4 py-3 text-center">
          <p className="text-surface-400 text-sm">🔒 Только по приглашению</p>
        </div>
      );
    }
    return (
      <div className="border-t border-surface-800 bg-surface-900 px-4 py-3 flex flex-col items-center gap-2">
        <div className="flex items-center gap-3">
          <p className="text-surface-400 text-sm">Вы не состоите в этом канале</p>
          <button
            disabled={joining}
            onClick={async () => {
              setJoining(true);
              setJoinError(null);
              try {
                await channelsApi.join(channelId);
                queryClient.invalidateQueries({ queryKey: ['channel', channelId] });
                queryClient.invalidateQueries({ queryKey: ['channels', 'mine'] });
              } catch (e: unknown) {
                const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
                setJoinError(msg ?? 'Ошибка при вступлении');
              } finally {
                setJoining(false);
              }
            }}
            className="btn-primary text-sm py-1.5 px-4 disabled:opacity-50"
          >
            {joining ? 'Вступаем...' : 'Вступить в канал'}
          </button>
        </div>
        {joinError && <p className="text-red-400 text-xs">{joinError}</p>}
      </div>
    );
  }

  const canPost = channel?.membersCanPost !== false || role === 'OWNER' || role === 'ADMIN' || role === 'MODERATOR';

  if (!canPost) {
    return (
      <div className="border-t border-surface-800 bg-surface-900 px-4 py-3 text-center">
        <p className="text-surface-400 text-sm">🔒 Только для чтения — писать могут только админы канала</p>
      </div>
    );
  }

  return (
    <MessageInput
      channelId={channelId}
      placeholder={`Написать в ${channel?.displayName || channel?.name || '...'}...`}
      replyTo={replyTo}
      onCancelReply={onCancelReply}
      mediaEnabled={channel?.mediaEnabled !== false}
      chatStyle={channel?.chatStyle}
      slowModeSeconds={(channel as any)?.slowModeSeconds ?? 0}
    />
  );
}
